/**
 * Live Agent events — sessions, execution modes, media and visual watchers.
 *
 * One slice of the `ANALYTICS_EVENTS` SSOT — see `../analytics.ts`, which
 * spreads every slice back into the single import surface call sites use.
 * The event NAME STRINGS are a PostHog dashboard contract: add and retire keys
 * here, never rename a value in place (see `rules/analytics.md`).
 */

export const LIVE_EVENTS = {
  // Live Mode
  // Started/Ended are SERVER-OWNED (agent-service live WS handler emits
  // them, consent-independent). Declared here for registry/grep affinity
  // only — the frontend must not emit them (double-count).
  // Started also carries client, app_version, build_number, update_id and
  // runtime_version. Ended also carries close_code and the per-session
  // counts photos_command, photos_device_still, photos_stream,
  // captures_requested, captures_saved, captures_failed, captures_pending,
  // captures_pending_at_end, recording_windows,
  // empty_turn_recoveries_fired_in_execution,
  // empty_turn_recoveries_held_in_execution, persona_scrubs, voice_commands
  // and tap_commands (agent-service constants/posthog_events.py).
  // captures_requested, photos_<rung>, captures_pending and captures_failed
  // count per model call (a throttled re-issue counts again); captures_saved
  // counts per unique photo; captures_pending_at_end is a teardown snapshot
  // of photos still saving.
  LIVE_SESSION_STARTED: 'Live Session Started',
  LIVE_SESSION_ENDED: 'Live Session Ended',
  // Dual-source: client emits with source='client' (media/WS errors the
  // server can't see); the server emits with source='server'.
  LIVE_SESSION_ERROR: 'Live Session Error',
  // Once-per-session client signal that the camera was activated — the
  // server cannot know this; replaces the used_video prop that lived on
  // the old client-side Session Ended event.
  LIVE_VIDEO_USED: 'Live Video Used',
  // The live page's shutter (`hooks/media/useLiveCapturePhoto.ts`): a device
  // photo saved as an attachment, or a failed take/save. Properties:
  // session_id; in_run on a save, stage ('take' | 'upload') on a failure;
  // as_artifact: true on a save reserved at the shutter (slice 3,
  // `capture_events` granted); never a title or an attachment id.
  LIVE_PHOTO_SAVED: 'Live Photo Saved',
  LIVE_PHOTO_SAVE_FAILED: 'Live Photo Save Failed',
  // A server recording window opened (`recording_state: started`, useLiveMode).
  // Properties: source ('voice_intent' | 'shutter': the shutter asked for it,
  // plan 2.8), kind, max_duration_seconds, request_id, target_fps.
  VIDEO_RECORDING_STARTED: 'Video Recording Started',
  VIDEO_RECORDING_ENDED: 'Video Recording Ended',
  // Mirrors the agent-service "Video Recording Failed" PostHog event so
  // a future client-side toast/banner can fire under the same name. Not
  // used at runtime yet; declared for symmetry + grep-ability.
  VIDEO_RECORDING_FAILED: 'Video Recording Failed',
  // A note said in a Live session, saved as text in the user's words
  // (agent-service `tools/add_note.py`, since 2026-10-16; it replaced the
  // recorded voice note and its 'Voice Note Captured' / 'Voice Note Failed').
  // Emitted by agent-service and mirrored here so it is greppable from one
  // place. Properties: platform, session_id, run_id_sent (a run id was sent,
  // not that the backend anchored it), source
  // ('voice' | 'typed' | 'capture_fallback': what the user said about a capture
  // that did not save); note_id on a save, error_code and outcome_known on a
  // failure. Never the note's words. Not used at runtime by the frontend.
  LIVE_NOTE_SAVED: 'Live Note Saved',
  LIVE_NOTE_FAILED: 'Live Note Failed',
  LIVE_STANDBY_ENTERED: 'Live Standby Entered',
  LIVE_STANDBY_EXITED: 'Live Standby Exited',
  // The live page's settings sheet (`components/live/LiveSettingsSheet.tsx`),
  // opened from the cog in the status band. Property: in_run.
  LIVE_SETTINGS_OPENED: 'Live Settings Opened',
  // The sheet's Auto-standby switch. Property: enabled.
  LIVE_AUTO_STANDBY_TOGGLED: 'Live Auto Standby Toggled',

  // Live Agent modes (PR-A through PR-F initiative). Fired by useLiveMode
  // on incoming mode_state / execution_state events.
  LIVE_MODE_SWITCHED: 'Live Mode Switched',
  EXECUTION_MODE_ENTERED: 'Execution Mode Entered',
  EXECUTION_MODE_EXITED: 'Execution Mode Exited',
  EXECUTION_STEP_COMPLETED: 'Execution Step Completed',
  // The two REQUEST halves of manual completion, fired by the step deck when
  // the user taps a pill. Deliberately NOT `Execution Step Completed`: that
  // one is the CONFIRMATION, fired from the `execution_state` frame the
  // server sends back, so emitting it on the tap as well would double-count
  // every manual completion and make the voice path look half as used as it
  // is. Kept as a separate pair so the gap between them is measurable — the
  // uplink is a plain text frame the agent has to interpret, and "the user
  // asked and the run never advanced" is the failure this surface can have.
  EXECUTION_STEP_COMPLETION_REQUESTED: 'Execution Step Completion Requested',
  EXECUTION_RUN_COMPLETION_REQUESTED: 'Execution Run Completion Requested',
  // CLIENT-emitted, by the web (`hooks/media/liveTapConfirmation.ts`) and the
  // Bower app under the same name and definition: a step deck Mark done tap
  // and the first server answer naming its command_id. Properties:
  // session_id, run_id, command_id, action, outcome (applied | resynced |
  // rejected | timeout), tap_to_state_ms (one device's clock, tap to
  // answer). Ids, enums and milliseconds only; never a step id.
  LIVE_TAP_CONFIRMED: 'Live Tap Confirmed',
  // CLIENT-emitted: the Live confirm card answered (check 2: ending a run
  // with steps open, or starting one over, needs a tap). Properties:
  // session_id, run_id, command (run.end | run.restart), answer (confirmed |
  // declined), sent (boolean: the answer's frame reached the socket; a
  // decline is sent too where the server offers command_decline).
  LIVE_COMMAND_CONFIRMATION_ANSWERED: 'Live Command Confirmation Answered',
  // CLIENT-emitted: a step card's skip, back or go-to was sent as a command
  // frame (Live command center slice 7). Its outcome is the server's
  // Execution Command Handled for the same command_id. Properties:
  // session_id, run_id, command_id, command (run.skip | run.back | run.goto),
  // protocol_gri. Never a step id. The Bower app sends it under the same name
  // with run_id, command_id, command and protocol_gri (no session_id), and
  // for the glasses button's double press command run.next with via button.
  LIVE_STEP_NAVIGATION_REQUESTED: 'Live Step Navigation Requested',
  // CLIENT-emitted: the Live shutter's PHOTO / VIDEO switch moved (plan 2.8).
  // Properties: session_id, mode (photo | video).
  LIVE_SHUTTER_MODE_SWITCHED: 'Live Shutter Mode Switched',
  // CLIENT-emitted: a video recorded in the browser on a Live session granted
  // `device_recording` (Live command center slice 6). SAVED once finalize
  // lands the clip; FAILED when it did not land. The browser is the only side
  // that knows why a clip stopped and how long it ran; the server's
  // `Live Recording Finalized` carries neither for a browser clip. A clip's
  // start is not tracked here: the server counts the commanded ones
  // (`Execution Command Handled`, video_start / video_stop). Properties:
  // session_id, started_by (shutter | server), stopped_by (shutter | server |
  // cap | camera | error | page), duration_ms, in_run; on SAVED also
  // size_bytes and parts (requests that carried bytes). Ids, enums and numbers
  // only.
  LIVE_DEVICE_RECORDING_SAVED: 'Live Device Recording Saved',
  LIVE_DEVICE_RECORDING_FAILED: 'Live Device Recording Failed',
  // SERVER-OWNED (agent-service `ExecutionCommandEvents.COMMAND_HANDLED`,
  // captured by `execution/announcer.py`): one per typed execution command,
  // landed or not. Properties: action (complete | skip | reopen | goto |
  // end_run | restart | video_start | video_stop; on an unknown_command
  // refusal, the device's command name, e.g. run.pause), source (tap | button | listener | voice),
  // outcome (applied | duplicate | stale | invalid | failed | closed |
  // needs_capture | held | replayed | unknown_command | ambiguous_step; a
  // device command answered with an ack reason counts the reason, e.g. busy,
  // not_in_plan, not_saved, step_not_in_run), hold
  // (echo_of_step | same_utterance | overlapped_announcement |
  // unanswered_voice_change | recent_advance, on held), platform, session_id,
  // reopened_run (boolean: the change reopened a finished run, the 10-minute
  // undo), run_id, command_id, step_index, steps_total, elapsed_ms, tap_kind
  // (typed | prose | stepless), confirmed_by (voice | button | tap, only on a
  // confirmed end or restart), and on voice speech_end_to_tool_ms,
  // tool_to_committed_ms, speech_end_source (activity_end | transcript |
  // none); never a step title or step id.
  // Declared here for grep affinity only; the frontend must not emit it.
  EXECUTION_COMMAND_HANDLED: 'Execution Command Handled',
  // SERVER-OWNED (agent-service `ExecutionCommandEvents.START_RUN_RESOLVED`,
  // captured by `tools/start_run.py`): one per voice `start_run`, and one per
  // `execution_start` client event (by `client_event`). Properties: outcome,
  // by, platform, session_id; never the query or a title. Declared here for grep affinity only; the
  // frontend must not emit it.
  EXECUTION_START_RUN_RESOLVED: 'Execution Start Run Resolved',
  // SERVER-OWNED (agent-service `ExecutionCommandEvents.RUN_RESUME_DECIDED`,
  // captured by `tools/start_run_resume.py`): one per start that found the
  // user's open run of that protocol from another session, and what was done
  // with it. Properties: decision, reason, steps_done_bucket, by, platform,
  // session_id; ids and enums only. Declared here for grep affinity only; the
  // frontend must not emit it.
  EXECUTION_RUN_RESUME_DECIDED: 'Execution Run Resume Decided',
  // SERVER-OWNED (agent-service `ExecutionCommandEvents.START_DERIVATION_WAIT`,
  // captured by `tools/switch_mode_todo.py`): a start that waited for the
  // protocol's steps to finish deriving. Properties: outcome (ready |
  // still_pending), waited_ms. Declared here for grep affinity only; the
  // frontend must not emit it.
  EXECUTION_START_DERIVATION_WAIT: 'Execution Start Derivation Wait',
  // SERVER-OWNED (agent-service `CaptureStepEvents`, capture steps at runtime):
  // a capture step's cue given (kind, step_kind, by; counted when offered, so
  // a held voice cue dropped because the step moved or the capture was taken
  // still counts), a capture step completed (kind, has_evidence, overridden,
  // by), and a video step's recording stopped because the step was left
  // (reason, owner, outcome). Ids and enums only.
  // Declared here for grep affinity only; the frontend must not emit them.
  CAPTURE_CUE_GIVEN: 'Capture Cue Given',
  CAPTURE_STEP_COMPLETED: 'Capture Step Completed',
  RECORDING_AUTO_STOPPED: 'Recording Auto Stopped',
  // SERVER-OWNED (backend `liveExecution.service.ts` `create`, the only writer
  // of a run row): exactly one per protocol run, after the row exists.
  // Properties: run_id, protocol_artifact_id, live_session_id, step_count,
  // platform, live_mode (stream | capture | voice), vision, camera, audio_out,
  // display, record_kind, todo_source (structured | markdown), fresh; ids and
  // codes only. Declared here for grep affinity
  // only; the frontend must not emit it.
  LIVE_EXECUTION_RUN_STARTED: 'Live Execution Run Started',
  // SERVER-OWNED (backend `liveExecution.missingCaptures.ts`
  // `recordMissingCaptures`): one per `capture_missing` step event newly
  // recorded, after the write lands, when a Live session closed while a capture
  // the agent requested on a step had not reached Bower. Properties:
  // workspace_id, run_id, protocol_artifact_id, capture (photo | video),
  // run_status; ids and enums only, never the step id (a slug of the step
  // title). Contract: LIVE_CAPTURE_EVENTS.MISSING in @bower/shared. Declared
  // here for grep affinity only; the frontend must not emit it.
  LIVE_CAPTURE_MISSING: 'Live Capture Missing',
  // SERVER-OWNED (backend `services/captures/liveCaptureSweep.ts`): a Live
  // capture reserved at its shutter (slice 3) whose file is still not in Bower
  // 24 hours later, once per capture. Properties: kind, by, transfer,
  // hours_since_shutter, attachment_id, workspace_id, run_id. Contract:
  // LIVE_CAPTURE_EVENTS.NOT_LANDED in @bower/shared. Declared here for grep
  // affinity only; the frontend must not emit it.
  LIVE_CAPTURE_NOT_LANDED: 'Live Capture Not Landed',
  // SERVER-OWNED (backend `services/captures/liveCaptureQuality*.ts`): a Live
  // capture's quality numbers, measured from its landed file (reference
  // 10.3), once per capture. Properties: kind, by, run_id, attachment_id,
  // workspace_id and the rounded numbers. Contract:
  // LIVE_CAPTURE_EVENTS.QUALITY_MEASURED in @bower/shared. Declared here for
  // grep affinity only; the frontend must not emit it.
  LIVE_CAPTURE_QUALITY_MEASURED: 'Live Capture Quality Measured',
  // SERVER-OWNED (agent-service `ExecutionCommandEvents.DEVICE_COMMAND_OUTCOME`,
  // captured by `execution/device_outcome.py`): one per device-command outcome
  // no other event reports. In time: capture_photo's DEVICE_UNAVAILABLE and a
  // client recording_stop; BUSY, KEPT and PENDING are `Live Voice Capture
  // Answered` instead (same code, same command_id). Late (after the server
  // stopped waiting): failures and kept captures as <OUTCOME>_AFTER_<VERDICT>
  // (FAILED_AFTER_PENDING, KEPT_AFTER_PENDING, a spoken start's
  // STARTED_AFTER_PENDING). A late saved photo is not here: it is the
  // backend's Live Capture Landed (same command_id) and `captures_saved` on
  // Live Session Ended; a late saved clip is Live Recording Finalized.
  // Properties: kind, code, platform, session_id, command_id (the agent's
  // `c_` id, the join key), and on a late photo outcome has_follow_up (a
  // boolean: the user asked for something to be done with the photo; never
  // the words); never a photo title. Declared here for grep
  // affinity only; the frontend must not emit it.
  LIVE_DEVICE_COMMAND_OUTCOME: 'Live Device Command Outcome',
  // Backend-emitted (`services/upload/liveCaptureLanded.ts`), once per Live
  // capture that lands in Bower storage. Properties: kind (photo | video |
  // audio), door (multipart | resumable), size_bytes,
  // capture_time_source (exif_zoned | exif_naive | client | client_naive |
  // none), shutter_to_landed_ms (zoned times only), attachment_id,
  // workspace_id, run_id. Contract: LIVE_CAPTURE_EVENTS in @bower/shared.
  // Declared here for grep affinity only; the frontend must not emit it.
  LIVE_CAPTURE_LANDED: 'Live Capture Landed',
  // SERVER-OWNED (agent-service `LiveSessionEvents.VOICE_CAPTURE_ANSWERED`,
  // captured by `tools/voice_capture_answered.py`): one per photo or spoken
  // start or stop recording sent to a device that owns the capture.
  // Properties: tool (capture_photo | start_recording | stop_recording), code
  // (the device's result code, OK for a saved photo, PENDING when the quick
  // wait expired, or the tool's error code), answer_ms, speech_end_to_tool_ms,
  // speech_end_source (activity_end | transcript | none), quick_ack (the
  // `live-command-center` flag; recordings only), platform, session_id.
  // Ids and enums only. Declared here for grep affinity only; the frontend
  // must not emit it.
  LIVE_VOICE_CAPTURE_ANSWERED: 'Live Voice Capture Answered',
  // PR-E: agent-service captures this on every confident proactive
  // classifier verdict (>= 0.8 confidence). Registered on the frontend
  // for the analytics constant registry's single-source-of-truth.
  PROACTIVE_STEP_DETECTION_TRIGGERED: 'Proactive Step Detection Triggered',
  // Video insights (flag-gated). Properties: mode, counts, booleans; never
  // step titles or model output.
  //
  // NOT 'Video Insight Requested' — that name belongs to the SERVER-emitted
  // event (`VIDEO_INSIGHT_EVENTS.REQUESTED` in
  // packages/shared/src/constants/extraction-events.ts), fired once per
  // accepted (202) request from videoInsight.controller.ts. This one fires on
  // the CLIENT the moment the user's request mutation resolves — it is the
  // user's intent-to-request, distinct from the backend's authoritative
  // "accepted and enqueued" record, and the two must never share a PostHog
  // event name or their series merge into one incoherent count.
  VIDEO_INSIGHT_REQUEST_INITIATED: 'Video Insight Request Initiated',
  // Resumable direct-to-GCS upload transport (size_bytes + stage only, no UGC).
  // VIDEO_* fires for video files (kept for the existing transcode-cost
  // dashboards); ATTACHMENT_* fires for every other type the unified resumable
  // transport now backs (PDFs, audio, docs, …) so they don't pollute the video
  // funnels. Gated on isVideoFile in services/upload/performUpload.ts.
  VIDEO_UPLOAD_STARTED: 'Video Upload Started',
  VIDEO_UPLOAD_COMPLETED: 'Video Upload Completed',
  VIDEO_UPLOAD_FAILED: 'Video Upload Failed',
  ATTACHMENT_UPLOAD_STARTED: 'Attachment Upload Started',
  ATTACHMENT_UPLOAD_COMPLETED: 'Attachment Upload Completed',
  ATTACHMENT_UPLOAD_FAILED: 'Attachment Upload Failed',
  // A preview the user opened could not be rendered, so they were shown the
  // download fallback instead. Properties: file_extension, preview_type
  // ('spreadsheet' | 'document' | 'html'), failure_reason ('fetch' | 'parse' |
  // 'empty'). For 'html', 'empty' means a page with nothing to show without its
  // scripts (a saved JavaScript app), and file_extension is html/htm/other.
  // Never the file name — that is UGC (rules/analytics.md) and PostHog is not
  // BAA-covered. Exists because a silently-empty preview area shipped and
  // survived unnoticed: the fetch half is only partly visible via
  // API_ERROR_OCCURRED (which skips 404, and an entity-ACL denial IS a 404),
  // and the parse half was fully dark.
  //
  // Two caveats before building a dashboard on this. (1) file_extension is an
  // EXTENSION; every other file-shaped property in this file (file_type) is a
  // MIME type, so the two don't join — the preview components only ever see a
  // name. (2) Coverage is 2 of ~5 preview surfaces: image, PDF and video still
  // fail silently, so a low rate here means thin instrumentation, not health.
  ATTACHMENT_PREVIEW_FAILED: 'Attachment Preview Failed',
  // A captured artifact's back-link: "Run 3 · X" → that run's page, or (an
  // older capture with no run) "Captured during X" → the protocol page.
  // { artifact_id, protocol_id, run_id }
  CAPTURE_BACKLINK_CLICKED: 'Capture Backlink Clicked',
  // A capture's location chip (attachment detail) → the coordinates on Google
  // Maps. Artifact id only, never the coordinates.
  CAPTURE_LOCATION_OPENED: 'Capture Location Opened',
  // Bird displayed a voice-requested image/video in the live session
  // (display_media / show_step_video tool → show_media event). IDs +
  // kind only, never content.
  LIVE_MEDIA_SHOWN: 'Live Media Shown',
  // ``Step Video Watched`` — a user opened a step's chapter via a Watch
  // affordance (drawer / strip during execution, or the authoring jump
  // buttons). Distinct from LIVE_MEDIA_SHOWN (the voice path). Watch-rate
  // per chapter is the engagement metric the flag ramp is judged on. IDs
  // only, no content. ``surface`` ∈ 'sheet' | 'authoring' at the call sites
  // that exist today: `LiveStepVideoSheet` emits 'sheet', the protocol
  // detail page's jump buttons emit 'authoring'. 'drawer' and 'strip' are
  // RETIRED — both surfaces are deleted — and are named here only because
  // historical rows carry them and a dashboard filtering the value needs to
  // know what it will find. There is deliberately no 'deck' value: a deck
  // card's Watch button opens the sheet, and the sheet is what reports.
  STEP_VIDEO_WATCHED: 'Step Video Watched',
  // Unacted change request — agent-service
  // plugins/unacted_change_request_plugin.py fires this when a turn whose
  // user message OPENED with a mutation verb ended with no function call
  // other than root's two read-only memory tools: no tool, no delegation.
  // The production half of PR #1735, where the coordinator answered a
  // repeated delete from its own conversation history and reported a note
  // as already in the Trash while it still existed. Read it as a RATE and
  // split on ``prior_turns`` — that fabrication answers from history so it
  // cannot occur at zero, while a first-turn hit is a refusal or a
  // clarifying question. Properties are a verb from a closed vocabulary of
  // OUR words plus a count; no part of the message travels. Listed here for
  // the same grep-affinity reason as the guard above, NOT because anything
  // in the frontend emits it.
  AGENT_CHANGE_REQUEST_UNACTED: 'Agent Change Request Unacted',
  // Visual watchers — user-defined "every time you see X, say Y" triggers.
  // Backend (agent-service) fires Set / Refused / Cleared / Fired. (A
  // server-side promise guard fired `Visual Watcher Guard Nudged` until it was
  // removed on 2026-10-07; historical rows survive in PostHog.) Frontend
  // constants kept in sync as the single grep-affinity source.
  //
  // PostHog property boundary — 2026-05-15 (PR #149): ``Visual
  // Watcher Set`` events gained ``source`` (``tool_call`` |
  // ``intent_classifier``, the latter historical only, with no emitter)
  // and ``dedup_hit`` (bool) properties.
  // The same boundary applied to the retired ``Protocol Annotation *``
  // events, whose constants left this file on 2026-09-19 with the
  // protocol-annotations subsystem; historical rows survive in PostHog.
  VISUAL_WATCHER_SET: 'Visual Watcher Set',
  VISUAL_WATCHER_REFUSED: 'Visual Watcher Refused',
  VISUAL_WATCHER_CLEARED: 'Visual Watcher Cleared',
  VISUAL_WATCHER_FIRED: 'Visual Watcher Fired',
  // Aggregate comparison outcomes only — never labels or image content.
  VISUAL_REFERENCE_CLASSIFICATION_COMPLETED: 'Visual Reference Classification Completed',

  // The Live-mode model picker (shown only to workspaces on the
  // `live-model-next` allowlist; 3.8 Live is everyone's default). Properties:
  // model_variant, one of 'default' | 'fallback' | 'default-thinking', a closed
  // vocabulary of our own strings, never user input. Until 2026-10-27 the same
  // three were 'next' | 'stable' | 'next-thinking' (renamed by role), so a
  // breakdown spanning that deploy shows both names for each arm.
  //
  // `'automatic'` was a fourth value until 2026-09-21 and CANNOT fire any
  // more: the menu line that produced it ("Default", earlier "Automatic
  // (workspace default)") resolved to the identical session as an explicit
  // 2.5 pick once the flag became permission-only, and was removed. A
  // breakdown on this property therefore goes to zero for that value at that
  // deploy — recorded here so the drop reads as a removal rather than as the
  // picker breaking.
  LIVE_MODEL_VARIANT_TOGGLED: 'Live Model Variant Toggled',

  // The Live-mode vision tier flipped: the client looks harder while the user
  // is speaking and returns to passive when the turn ends. Properties: tier,
  // reason ('speech_onset' | 'turn_complete' | 'session_ended'), max_edge_px,
  // fps, attentive_ms (only on the closing transition).
  //
  // The reason is what separates "a researcher asked something" from "acoustic
  // echo tripped it" in the Phase 3 report, and they need different fixes.
  // Closed vocabularies and numbers only — never user input, never a frame.
  LIVE_VISION_TIER_CHANGED: 'Live Vision Tier Changed',

  // A finished user transcription looked like an acoustic echo of Bird's own
  // playback (see `utils/selfEchoDetection.ts`) — the RESPONSE_REJECTED /
  // phantom-turn class traced to session `9297140a…` (2026-09-21). Measures
  // the self-echo rate with and without the AEC-loopback mitigation
  // (`aec_loopback_active`); never the transcript text itself (UGC).
  //
  // `is_agent_speaking` and `ms_since_agent_speech_ended` are read off LOCAL
  // playback: whether the reply is still playing out, and how long since it
  // finished (null while it plays). They used to be timed from the server's
  // `turn_complete`, which arrives with seconds of the reply still queued, so
  // the gate missed echo heard mid-playback. It still misses echo captioned
  // more than 1.5 s after a barge-in cut, the 2026-09-24 staging runs' shape.
  // A rise in the rate at that deploy is the fix, not more echo; a flat rate
  // does not mean it failed, if web echo is mostly the post-cut kind.
  LIVE_SELF_ECHO_SUSPECTED: 'Live Self Echo Suspected',
  // Once per connect(): `active` says whether the AEC-loopback playback path
  // (`utils/audioUtils.ts::connectViaAecLoopback`) engaged or fell back to
  // direct playback. Every session attempts it; `active: false` means setup
  // failed (see `AudioPlaybackController.aecLoopbackActive`). Pairs with the
  // event above to split the self-echo rate by whether the mitigation ran.
  LIVE_PLAYBACK_AEC_LOOPBACK_STATUS: 'Live Playback AEC Loopback Status',
  // The run header's transcript pause/resume switch, counted when the
  // server's echo confirms the change the user asked for. Property: enabled
  // only, never transcript text.
  LIVE_TRANSCRIPT_TOGGLED: 'Live Transcript Toggled',
  // An owner or admin turned the workspace's Live run transcripts on or off in
  // Workspace settings, confirmed by the server. Property: enabled only.
  LIVE_TRANSCRIPT_WORKSPACE_SETTING_CHANGED: 'Live Transcript Workspace Setting Changed',
  // "View original transcript" opened on a Live run's transcript note.
  // Property: artifact_id only, never transcript text.
  LIVE_TRANSCRIPT_ORIGINAL_VIEWED: 'Live Transcript Original Viewed',
  // The `seen` clock point (Live command center reference 10.1): an open run
  // page rendered a run change pushed over `/sync`.
  // Properties: run_id, ms_since_event (receipt to render, this device's
  // clock), ms_since_broadcast (the server's send time to render, corrected by
  // clock_offset_ms when a contract-3 Live session sampled one, else across
  // two clocks), clock_offset_ms (or null). Ids and numbers only.
  LIVE_RUN_SEEN: 'Live Run Seen',
} as const;
